import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  Typography,
} from "@mui/material";
import { grey, yellow } from "@mui/material/colors";
import { OrganizationUserContext } from "contexts/Organization";
import { SlideAnimationContext } from "contexts/SlideAnimation";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BentoCategory,
  OrganizationNiche,
  OrganizationNicheStatus,
  OrganizationVaProfile,
  TemplateIntakeForm,
} from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import ExcludeIncludeFilters from "features/Influencer/Profile/RecommendationForm/ExcludeIncludeFilters";
import { fetcherAuth } from "utils/api";
import { FREE_NICHES } from "utils/influencer";
import { makeDeepCopy } from "utils/updateLocalState";

import { SaveOrgVaProfileFunc } from "../TemplatesPick/schema";
import { vaSelfServeNavigate } from "../helpers";
import Niche from "./Niche";

interface Props {
  categoriesToPickFrom: BentoCategory[];
  numOfNiche: number;
  saveOrgVaProfile: SaveOrgVaProfileFunc;
  organizationVaProfile: OrganizationVaProfile | null;
  setOrganizationVaProfile: Dispatch<
    SetStateAction<OrganizationVaProfile | null>
  >;
  templateIntakeForms?: TemplateIntakeForm[];
}

const NichePick = ({
  categoriesToPickFrom,
  numOfNiche,
  saveOrgVaProfile,
  organizationVaProfile,
  setOrganizationVaProfile,
  templateIntakeForms,
}: Props) => {
  const { getToken } = useAuth();
  const location = useLocation();
  const { currentOrg } = useContext(OrganizationUserContext);
  const slideContext = useContext(SlideAnimationContext);
  const [niches, setNiches] = useState<OrganizationNiche[]>([]);
  const navigate = useNavigate();

  const [excludeIncludeFilterHasChanged, setExcludeIncludeFilterHasChanged] =
    useState(false);
  const [personalizeOutreach, setPersonalizeOutreach] = useState(false);

  const handleNext = async (e: CustomEvent) => {
    e.preventDefault();
    const copy = makeDeepCopy(organizationVaProfile || {});
    copy.niches = niches;
    copy.personalizeOutreach = personalizeOutreach;

    setOrganizationVaProfile(copy);
    saveOrgVaProfile(copy, excludeIncludeFilterHasChanged, true, true);
  };

  const handleBack = async () => {
    if (slideContext) {
      await slideContext.slideOutDown();
      slideContext.slideInDown();
    }
    navigate(
      `/${vaSelfServeNavigate(location.pathname, "back")}${location.search}`,
    );
  };

  const initializeNiches = async () => {
    const { niches: currentNiches } = await fetcherAuth(
      getToken,
      `/api/organization/${currentOrg?.id}/organization-niches`,
    );
    if (currentNiches.length > 0) {
      setNiches(currentNiches);
      return;
    }

    let initializeNiches: OrganizationNiche[] = [];
    for (let i = 0; i < numOfNiche + FREE_NICHES; i++) {
      initializeNiches.push({
        name: `Niche ${i + 1}`,
        notes: "",
        status: OrganizationNicheStatus.pending,
      });

      setNiches(initializeNiches);
    }
  };

  const initializePersonalizeOutreach = () => {
    if (organizationVaProfile?.personalizeOutreach) {
      setPersonalizeOutreach(true);
    }
  };

  useEffect(() => {
    initializeNiches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numOfNiche]);

  useEffect(() => {
    initializePersonalizeOutreach();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationVaProfile?.personalizeOutreach]);

  return (
    <>
      <Typography variant="h3" gutterBottom sx={{ textAlign: "center", mt: 0 }}>
        Step 1: Select Your Niches
      </Typography>

      <form onSubmit={handleNext}>
        {Array.from({ length: niches?.length }, (_, index) => (
          <Niche
            key={index}
            niche={niches[index]}
            niches={niches}
            setNiches={setNiches}
            index={index}
            categoriesToPickFrom={categoriesToPickFrom}
            templateIntakeForms={templateIntakeForms}
          />
        ))}

        <Typography variant="h6" sx={{ fontSize: 14 }}>
          More customizations
        </Typography>

        <ExcludeIncludeFilters
          setExcludeIncludeFilterHasChanged={setExcludeIncludeFilterHasChanged}
          hideSecondaryText
        />

        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="h6" sx={{ fontSize: 14, mb: 1 }}>
            AI Personalization
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={personalizeOutreach}
                onChange={(e) => setPersonalizeOutreach(e.target.checked)}
                icon={<Box component="i" className="fa-regular fa-square" />}
                checkedIcon={
                  <Box component="i" className="fa-solid fa-check-square" />
                }
              />
            }
            label="Use AI to personalize my outreach emails"
          />

          <FormHelperText sx={{ color: grey[700] }}>
            Your email will be automatically personalized to better match each
            brand's industry, improving relevance and reducing mismatches.{" "}
            <Box
              component="span"
              sx={{ color: yellow[800] }}
              className="fa-solid fa-triangle-exclamation"
            />{" "}
            However, AI may occasionally include inaccurate details or make the
            email sound less natural than your original template.
          </FormHelperText>
        </Box>

        <Grid item xs="auto" container columnGap={1} sx={{ mt: 4 }}>
          <Grid item xs="auto">
            <Button disableElevation onClick={handleBack}>
              <Box
                component="i"
                className="fa-solid fa-arrow-left"
                sx={{ mr: 1 }}
              />
              Back
            </Button>
          </Grid>
          <Grid item xs></Grid>
          <Grid item xs="auto">
            <Button disableElevation type="submit">
              Next
              <Box
                component="i"
                className="fa-solid fa-arrow-right"
                sx={{ ml: 1 }}
              />
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export default NichePick;
